import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../api';
import {
  LayoutDashboard,
  FileText,
  ListChecks,
  ClipboardCheck,
  BarChart3,
  Shield,
  Target,
  Gauge,
  Truck,
  Search,
  HardHat,
  MessageSquareWarning,
  RefreshCw,
  Users,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  LogOut,
  Menu,
  User,
  Settings,
  Building2,
  Database,
  BookType,
  GitBranch,
  BadgeCheck,
  History,
  CalendarRange,
  Info,
  Scale,
  ShieldCheck,
  ShieldAlert,
  UserCheck,
  X,
  Landmark,
  Crown,
  NotebookPen,
  FolderOpen,
  ClipboardList,
  AlertTriangle
} from 'lucide-react';

const menuItems = [
  { name: 'Dashboard',             icon: LayoutDashboard,      path: '/',                  moduleCode: 'dashboard' },
  { name: 'Gestão de Documentos',  icon: FileText,             path: '/documentos',        moduleCode: 'documents' },
  { name: 'Lista Mestra',          icon: ListChecks,           path: '/lista-mestra',      moduleCode: 'master_list' },
  { name: 'Documentos Externos',   icon: FolderOpen,           path: '/documentos-externos', moduleCode: 'external_documents' },
  { name: 'Controle de Mudanças',  icon: RefreshCw,            path: '/controle-mudancas', moduleCode: 'change_control' },
  { name: 'Gestão de Não Conformidades', icon: AlertTriangle,  path: '/nao-conformidades', moduleCode: 'non_conformities' },
  { name: 'SACPM',                 icon: ClipboardCheck,       path: '/sacpm',             moduleCode: 'sacpm' },
  { name: 'Auditorias',            icon: Search,               path: '/auditorias',        moduleCode: 'audits' },
  { name: 'Indicadores',           icon: BarChart3,            path: '/indicadores',       moduleCode: 'indicators' },
  { name: 'Gestão de Riscos',      icon: Shield,               path: '/riscos',            moduleCode: 'risks' },
  { name: 'Análise SWOT',          icon: Target,               path: '/swot',              moduleCode: 'swot' },
  {
    name: 'ATA',
    icon: Landmark,
    moduleCode: 'top_management',
    children: [
      { name: 'Alta Direção',    path: '/alta-direcao', icon: Crown,       moduleCode: 'top_management' },
      { name: 'Ata de Reunião',  path: '/ata-reuniao',  icon: NotebookPen, moduleCode: 'meeting_minutes' },
    ]
  },
  {
    name: 'Gestão de SST',
    icon: HardHat,
    moduleCode: 'sst',
    children: [
      { name: 'Ocorrências e EPIs', path: '/sst', icon: HardHat, moduleCode: 'sst' },
      { name: 'Segurança do Trabalho', path: '/seguranca-trabalho', icon: ShieldAlert, moduleCode: 'sst' },
    ]
  },
  {
    name: 'Regulatório',
    icon: Scale,
    moduleCode: 'calibrations',
    children: [
      { name: 'Calibrações',    path: '/calibracoes',    icon: Gauge,          moduleCode: 'calibrations' },
      { name: 'Fornecedores',   path: '/fornecedores',   icon: Truck,          moduleCode: 'suppliers' },
      // Treinamentos: item de menu ocultado propositalmente (página/rota mantidas para uso futuro)
      { name: 'Auto Inspeção',  path: '/auto-inspecao',  icon: ClipboardCheck, moduleCode: 'inspections' },
      { name: 'Cliente Qualificado', path: '/cliente-qualificado', icon: UserCheck, moduleCode: 'client_qualification' },
      { name: 'Análise e Crítica', path: '/analise-critica', icon: ClipboardList, moduleCode: 'client_requirement_analysis' },
      { name: 'Controle', icon: ShieldCheck, path: '/controle', moduleCode: 'controle' },
      { name: 'Qualificações', icon: BadgeCheck, path: '/qualificacoes', moduleCode: 'qualificacoes' },
    ]
  },
  { name: 'Reclamações',           icon: MessageSquareWarning, path: '/reclamacoes',       moduleCode: 'complaints' },
  {
    name: 'Cadastros',
    icon: Database,
    moduleCode: 'sectors',
    children: [
      { name: 'Setores',    path: '/cadastros/setores',    icon: Building2, moduleCode: 'sectors' },
      { name: 'Tipos',      path: '/cadastros/tipos',      icon: BookType,  moduleCode: 'types' },
      { name: 'Processos',  path: '/cadastros/processos',  icon: GitBranch, moduleCode: 'processes' },
      { name: 'Certificações', path: '/cadastros/certificacoes', icon: BadgeCheck, moduleCode: 'certificates' },
    ]
  },
  {
    name: 'Usuários e Perfis',
    icon: Users,
    moduleCode: 'users',
    children: [
      { name: 'Usuários', path: '/usuarios', moduleCode: 'users' },
      { name: 'Perfis',   path: '/perfis',   moduleCode: 'profiles' },
    ]
  },
  {
    name: 'Conformidade',
    icon: Shield,
    moduleCode: 'dashboard',
    children: [
      { name: 'Trilha de Auditoria', path: '/trilha-auditoria',  icon: History,       moduleCode: 'dashboard' },
      { name: 'Revisão Periódica',   path: '/revisao-periodica', icon: CalendarRange, moduleCode: 'dashboard' },
      { name: 'Sobre o Sistema',     path: '/sobre',             icon: Info,          moduleCode: 'dashboard' },
    ]
  },
  { name: 'Configurações',         icon: Settings,             path: '/configuracoes',     moduleCode: 'settings' },
];

// O arquivo /logo-qualysynx.png é o ícone (símbolo) já enquadrado em um
// PNG quadrado — escrevemos "QualySynX" ao lado, em HTML, nas mesmas cores
// da marca, para o texto ter seu próprio espaço e nunca ficar espremido.
const LOGO_SRC = '/logo-qualysynx.png';

export const BrandMark = ({ size = 56, showText = true, textSize = 21 }) => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: showText ? '0.5rem' : 0 }}>
      <img
        src={LOGO_SRC}
        alt="QualySynX"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          flexShrink: 0,
          objectFit: 'contain',
        }}
      />
      {showText && (
        <span style={{ fontFamily: "'DM Sans', sans-serif", fontVariationSettings: "'opsz' 9", fontSize: `${textSize}px`, fontWeight: 400, lineHeight: 1.0286, letterSpacing: '-0.5px', whiteSpace: 'nowrap' }}>
          <span style={{ color: '#FFFFFF' }}>Qualy</span>
          <span style={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #22D3EE 0%, #2563EB 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
          }}>SynX</span>
        </span>
      )}
    </div>
  );
};

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(['Usuários e Perfis']);
  const [menuSearch, setMenuSearch] = useState('');
  // Abaixo de 1024px (tablets em pé e celulares) o menu vira uma gaveta
  // sobreposta em vez de empurrar o conteúdo — não faz sentido "recolher"
  // o menu para ícones em telas de toque estreitas.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 1024 : false
  );

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fecha a gaveta ao navegar para uma nova rota no mobile
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const offset = isMobile ? '0px' : (sidebarOpen ? '130px' : '36px');
    document.documentElement.style.setProperty('--sidebar-offset', offset);
  }, [sidebarOpen, isMobile]);

  const hasPermission = (moduleCode) => {
    if (!user) return false;
    if (user.is_admin) return true;
    if (!moduleCode) return true;
    const codes = Array.isArray(moduleCode) ? moduleCode : [moduleCode];
    return codes.some(code => user.permissions?.some(p => p.permission?.module_code === code && p.can_view)) ?? false;
  };

  // Recursively filters menu items (and nested sub-groups) by permission
  const filterByPermission = (item) => {
    if (item.children) {
      const visibleChildren = item.children.map(filterByPermission).filter(Boolean);
      if (visibleChildren.length === 0) return null;
      return { ...item, children: visibleChildren };
    }
    return hasPermission(item.moduleCode) ? item : null;
  };

  const visibleItems = menuItems.map(filterByPermission).filter(Boolean);

  // Removes accents/diacritics so search works regardless of special characters (ex: "gestao" ~ "Gestão")
  const DIACRITICS_REGEX = new RegExp('[̀-ͯ]', 'g');
  const normalize = (str) => str
    .normalize('NFD')
    .replace(DIACRITICS_REGEX, '')
    .toLowerCase();

  // Recursively filters menu items (and nested sub-groups) by the search term
  const filterBySearch = (item, term) => {
    if (!term) return item;
    const nameMatches = normalize(item.name).includes(term);
    if (item.children) {
      if (nameMatches) return item;
      const visibleChildren = item.children.map(child => filterBySearch(child, term)).filter(Boolean);
      if (visibleChildren.length === 0) return null;
      return { ...item, children: visibleChildren };
    }
    return nameMatches ? item : null;
  };

  const searchTerm = menuSearch.trim().toLowerCase();
  const searchedItems = searchTerm
    ? visibleItems.map(item => filterBySearch(item, searchTerm)).filter(Boolean)
    : visibleItems;

  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev =>
      prev.includes(menuKey) ? prev.filter(m => m !== menuKey) : [...prev, menuKey]
    );
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  // No mobile a gaveta é sempre exibida "expandida" (com rótulos); o modo
  // ícone-apenas (sidebarOpen=false) só existe no rail fixo do desktop.
  const expanded = isMobile ? true : sidebarOpen;

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', display: 'flex' }}>
      {/* Mobile overlay */}
      {isMobile && mobileMenuOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 40, backdropFilter: 'blur(4px)' }}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      <aside style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: 50,
        width: isMobile ? '82vw' : (sidebarOpen ? '260px' : '72px'),
        maxWidth: isMobile ? '300px' : undefined,
        background: 'linear-gradient(180deg, #14233D 0%, #0f1c31 100%)',
        borderRight: '1px solid rgba(37,99,235,0.18)',
        display: 'flex',
        flexDirection: 'column',
        transform: isMobile ? `translateX(${mobileMenuOpen ? '0' : '-100%'})` : 'none',
        transition: isMobile
          ? 'transform 0.3s cubic-bezier(0.4,0,0.2,1)'
          : 'width 0.3s cubic-bezier(0.4,0,0.2,1)',
        boxShadow: '4px 0 24px rgba(0,0,0,0.4)',
      }}>

        {/* Logo area + controls */}
        <div style={{
          height: expanded ? '96px' : '128px',
          display: 'flex',
          flexDirection: expanded ? 'row' : 'column',
          alignItems: 'center',
          justifyContent: expanded ? 'space-between' : 'flex-start',
          borderBottom: '1px solid rgba(37,99,235,0.15)',
          flexShrink: 0,
          transition: 'height 0.3s cubic-bezier(0.4,0,0.2,1)',
          padding: expanded ? '8px 8px 8px 16px' : '12px 8px',
          boxSizing: 'border-box',
          gap: expanded ? 0 : '0.65rem',
        }}>
          <div style={{ margin: isMobile || expanded ? 0 : '0 auto' }}>
            <BrandMark size={expanded ? 48 : 40} showText={expanded} textSize={21} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
            {isMobile ? (
              <button
                onClick={() => setMobileMenuOpen(false)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '0.4rem', flexShrink: 0 }}
                aria-label="Fechar menu"
              >
                <X size={22} />
              </button>
            ) : (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                style={{
                  padding: '0.5rem',
                  background: 'rgba(37,99,235,0.08)',
                  border: '1px solid rgba(37,99,235,0.2)',
                  borderRadius: '0.625rem',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  flexShrink: 0,
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#00D4CB'; e.currentTarget.style.borderColor = 'rgba(0,212,203,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.borderColor = 'rgba(37,99,235,0.2)'; }}
                title={sidebarOpen ? 'Recolher menu' : 'Expandir menu'}
              >
                {sidebarOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
              </button>
            )}
          </div>
        </div>

        {/* Search */}
        <div style={{ padding: expanded ? '0.75rem 0.75rem 0' : '0.75rem 0 0', flexShrink: 0 }}>
          {expanded ? (
            <div style={{ position: 'relative' }}>
              <Search size={15} style={{ position: 'absolute', left: '0.65rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b', pointerEvents: 'none' }} />
              <input
                type="text"
                value={menuSearch}
                onChange={e => setMenuSearch(e.target.value)}
                placeholder="Buscar no menu..."
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '0.5rem 2rem 0.5rem 2.1rem',
                  borderRadius: '0.625rem',
                  border: '1px solid rgba(37,99,235,0.2)',
                  background: 'rgba(37,99,235,0.08)',
                  color: '#f1f5f9',
                  fontSize: '0.8rem',
                  outline: 'none',
                }}
                onFocus={e => e.currentTarget.style.borderColor = '#2563EB'}
                onBlur={e => e.currentTarget.style.borderColor = 'rgba(37,99,235,0.2)'}
              />
              {menuSearch && (
                <button
                  onClick={() => setMenuSearch('')}
                  style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', display: 'flex' }}
                  title="Limpar busca"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={() => setSidebarOpen(true)}
              style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0.5rem 0', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}
              title="Buscar no menu"
              onMouseEnter={e => e.currentTarget.style.color = '#00D4CB'}
              onMouseLeave={e => e.currentTarget.style.color = '#64748b'}
            >
              <Search size={18} />
            </button>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 0.625rem', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {searchedItems.length === 0 && (
            <p style={{ padding: '0.75rem', fontSize: '0.8rem', color: '#64748b', textAlign: 'center' }}>
              Nenhum item encontrado.
            </p>
          )}
          {searchedItems.map((item, index) => (
            <div key={index}>
              {item.children ? (
                <div>
                  <button
                    onClick={() => toggleMenu(item.name)}
                    className="sidebar-link"
                    style={{ width: '100%', justifyContent: expanded ? 'space-between' : 'center', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <item.icon size={18} style={{ flexShrink: 0 }} />
                      {expanded && <span style={{ whiteSpace: 'nowrap' }}>{item.name}</span>}
                    </div>
                    {expanded && (expandedMenus.includes(item.name) ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
                  </button>
                  {expanded && (!!searchTerm || expandedMenus.includes(item.name)) && (
                    <div style={{ marginLeft: '2.25rem', marginTop: '2px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
                      {item.children.map((child, ci) => {
                        if (child.children) {
                          const subKey = `${item.name}/${child.name}`;
                          return (
                            <div key={ci}>
                              <button
                                onClick={() => toggleMenu(subKey)}
                                style={{
                                  width: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '0.5rem 0.75rem',
                                  borderRadius: '0.625rem',
                                  fontSize: '0.825rem',
                                  color: '#94a3b8',
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                }}
                              >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                  {child.icon && <child.icon size={15} style={{ flexShrink: 0 }} />}
                                  {child.name}
                                </span>
                                {expandedMenus.includes(subKey) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                              </button>
                              {(!!searchTerm || expandedMenus.includes(subKey)) && (
                                <div style={{ marginLeft: '1.5rem', marginTop: '2px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                  {child.children.map((grandchild, gi) => (
                                    <NavLink
                                      key={gi}
                                      to={grandchild.path}
                                      style={({ isActive }) => ({
                                        display: 'block',
                                        padding: '0.5rem 0.75rem',
                                        borderRadius: '0.625rem',
                                        fontSize: '0.8rem',
                                        textDecoration: 'none',
                                        color: isActive ? '#00D4CB' : '#94a3b8',
                                        background: isActive ? 'rgba(0,212,203,0.1)' : 'transparent',
                                        fontWeight: isActive ? 600 : 400,
                                        borderLeft: isActive ? '2px solid #00D4CB' : '2px solid transparent',
                                        transition: 'all 0.15s',
                                      })}
                                    >
                                      {grandchild.name}
                                    </NavLink>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        }
                        return (
                          <NavLink
                            key={ci}
                            to={child.path}
                            style={({ isActive }) => ({
                              display: 'block',
                              padding: '0.5rem 0.75rem',
                              borderRadius: '0.625rem',
                              fontSize: '0.825rem',
                              textDecoration: 'none',
                              color: isActive ? '#00D4CB' : '#94a3b8',
                              background: isActive ? 'rgba(0,212,203,0.1)' : 'transparent',
                              fontWeight: isActive ? 600 : 400,
                              borderLeft: isActive ? '2px solid #00D4CB' : '2px solid transparent',
                              transition: 'all 0.15s',
                            })}
                          >
                            {child.name}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  to={item.path}
                  className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
                  style={{ justifyContent: expanded ? 'flex-start' : 'center' }}
                  title={!expanded ? item.name : undefined}
                >
                  <item.icon size={18} style={{ flexShrink: 0 }} />
                  {expanded && <span style={{ whiteSpace: 'nowrap' }}>{item.name}</span>}
                </NavLink>
              )}
            </div>
          ))}
        </nav>

        {/* User area — nome/cargo, avatar e sair no rodapé da sidebar */}
        <div style={{
          borderTop: '1px solid rgba(37,99,235,0.15)',
          padding: expanded ? '0.75rem' : '0.75rem 0.5rem',
          flexShrink: 0,
          display: 'flex',
          flexDirection: expanded ? 'row' : 'column',
          alignItems: 'center',
          justifyContent: expanded ? 'space-between' : 'center',
          gap: '0.5rem',
        }}>
          <button
            onClick={() => navigate('/meu-perfil')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem',
              minWidth: 0,
              flex: 1,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
              textAlign: 'left',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            title="Meu Perfil"
          >
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #2563EB, #00D4CB)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 0 0 2px rgba(0,212,203,0.3)',
            }}>
              <User size={16} style={{ color: '#fff' }} />
            </div>
            {expanded && (
              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                <span style={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#f1f5f9',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                }}>
                  {user?.full_name}
                </span>
                <span style={{ fontSize: '0.7rem', color: '#00D4CB' }}>
                  {user?.position || 'Usuário'}
                </span>
              </div>
            )}
          </button>

          <button
            onClick={handleLogout}
            style={{
              padding: '0.5rem',
              background: 'transparent',
              border: '1px solid rgba(239,68,68,0.2)',
              borderRadius: '0.625rem',
              color: '#64748b',
              cursor: 'pointer',
              transition: 'all 0.2s',
              flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#f87171'; e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.2)'; }}
            title="Sair"
          >
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      {/* ── Main ────────────────────────────────────────────────────── */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        minWidth: 0,
        marginLeft: isMobile ? 0 : (sidebarOpen ? '260px' : '72px'),
        paddingTop: isMobile ? '56px' : 0,
        transition: 'margin-left 0.3s cubic-bezier(0.4,0,0.2,1)',
        background: '#F8FAFC',
      }}>

        {/* Barra fixa no topo no mobile: hambúrguer + logo, nunca sobrepõe o
            conteúdo da página (ex.: título "Dashboard" + botão "Atualizar") */}
        {isMobile && (
          <header style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '56px',
            zIndex: 45,
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0 0.75rem',
            background: 'rgba(20,35,61,0.97)',
            borderBottom: '1px solid rgba(37,99,235,0.2)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
            backdropFilter: 'blur(6px)',
          }}>
            {!mobileMenuOpen && (
              <button
                onClick={() => setMobileMenuOpen(true)}
                style={{
                  flexShrink: 0,
                  padding: '0.5rem',
                  background: 'rgba(37,99,235,0.1)',
                  border: '1px solid rgba(37,99,235,0.25)',
                  borderRadius: '0.625rem',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onMouseEnter={e => e.currentTarget.style.color = '#00D4CB'}
                onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                aria-label="Abrir menu"
              >
                <Menu size={20} />
              </button>
            )}
            <BrandMark size={36} showText textSize={16} />
          </header>
        )}

        {/* Page Content */}
        <main style={{ flex: 1, padding: isMobile ? '1.25rem' : '1.5rem', overflowX: 'hidden', minWidth: 0 }}>
          <Outlet />
        </main>

        {/* Footer */}
        <footer style={{
          height: '44px',
          background: '#FFFFFF',
          borderTop: '1px solid #E2E8F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <p style={{ fontSize: '0.7rem', color: '#64748B', letterSpacing: '0.04em' }}>
            © 2025 <span style={{ fontFamily: "'DM Sans', sans-serif", fontVariationSettings: "'opsz' 9", color: '#0D9488' }}>Qualy<span style={{ fontWeight: 700 }}>SynX</span></span> SGQ — v1.0.0 — Gestão Inteligente da Qualidade
          </p>
        </footer>
      </div>
    </div>
  );
};

export default Layout;